import { create } from 'zustand';
import type { SampledVector } from '../lib/hermite';
import { fetchManyVectors, jdToIso } from '../lib/ephemeris';

export interface PlanetSpec {
  id: string;          // ID Horizons
  name: string;
  color: string;
  radius: number;      // taille rendu (visuel, non physique)
}

export const PLANETS: PlanetSpec[] = [
  { id: '199', name: 'Mercure',  color: '#b9aa92', radius: 0.05 },
  { id: '299', name: 'Vénus',    color: '#e8c98a', radius: 0.09 },
  { id: '399', name: 'Terre',    color: '#7aa7ff', radius: 0.10 },
  { id: '499', name: 'Mars',     color: '#d97a55', radius: 0.07 },
  { id: '599', name: 'Jupiter',  color: '#e3c590', radius: 0.30 },
  { id: '699', name: 'Saturne',  color: '#dcc88f', radius: 0.26 },
  { id: '799', name: 'Uranus',   color: '#9fdcdc', radius: 0.18 },
  { id: '899', name: 'Neptune',  color: '#5b8ad4', radius: 0.18 },
];

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

interface PlanetsState {
  vectors: Record<string, SampledVector[]>;
  status: LoadStatus;
  error: string | null;
  loadedRange: { startJd: number; stopJd: number } | null;
  loadAround: (jd: number, daysBefore: number, daysAfter: number) => Promise<void>;
}

export const usePlanetsStore = create<PlanetsState>((set, get) => ({
  vectors: {},
  status: 'idle',
  error: null,
  loadedRange: null,

  loadAround: async (jd, daysBefore, daysAfter) => {
    const startJd = jd - daysBefore;
    const stopJd = jd + daysAfter;

    const cur = get().loadedRange;
    if (
      cur &&
      cur.startJd <= startJd &&
      cur.stopJd >= stopJd &&
      get().status === 'ready'
    ) {
      return;
    }

    set({ status: 'loading', error: null });
    try {
      const vectors = await fetchManyVectors(
        PLANETS.map((p) => p.id),
        jdToIso(startJd),
        jdToIso(stopJd),
        '1d',
      );
      const fetchedCount = Object.keys(vectors).length;
      if (fetchedCount === 0) {
        throw new Error('Aucune cible récupérée — backend indisponible ?');
      }
      set({
        vectors,
        status: 'ready',
        loadedRange: { startJd, stopJd },
      });
    } catch (e) {
      set({ status: 'error', error: String(e) });
    }
  },
}));
